import { Link, useLocation } from "@tanstack/react-router";
import {
	ArrowLeft,
	ChevronRight,
	CircleUser,
	LayoutDashboard,
	LogIn,
	PanelLeftClose,
	PanelLeftOpen,
	Settings,
} from "lucide-react";
import { type ComponentType, type ReactNode, useEffect, useRef } from "react";
import { useAttentionInbox } from "@/components/activity/attention-inbox";
import { LobuSearchIcon } from "@/components/ui/lobu-nav-icons";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { ApiError } from "@/lib/api/core";
import { requestErrorCopy } from "@/lib/api/error-copy";
import { useAuthState } from "@/lib/auth-state";
import { isOwnerSectionActive, OWNER_SECTIONS } from "@/lib/owner-sections";
import { currentCallbackUrl, signInSearch } from "@/lib/sign-in-redirect";
import { cn } from "@/lib/utils";
import { PRIMARY_SECTIONS } from "@/lib/workspace-navigation";
import { OnboardingChecklist } from "./onboarding-checklist";
import { OrganizationDropdown } from "./organization-dropdown";
import { SidebarSignIn } from "./sidebar-sign-in";
import { useWorkspaceNavigation } from "./use-workspace-navigation";

type IconComponent = ComponentType<{ className?: string }>;

function openCommandPalette() {
	document.dispatchEvent(
		new KeyboardEvent("keydown", { key: "k", metaKey: true, bubbles: true }),
	);
}

/** Three stable, labeled destinations for the narrow shell. */
export function CompactTopNav() {
	const { owner, isMember, isMembershipLoading, settings, location } =
		useWorkspaceNavigation();
	return (
		<TooltipProvider delayDuration={200}>
			<div className="flex min-w-0 flex-1 items-center gap-1">
				<OrganizationDropdown display="icon" />
				{owner ? (
					<>
						<nav
							aria-label="Workspace sections"
							className="flex min-w-0 flex-1 items-center justify-center gap-0.5"
						>
							{PRIMARY_SECTIONS.map((key) => {
								const section = OWNER_SECTIONS[key];
								const active =
									!settings &&
									isOwnerSectionActive(key, owner, location.pathname);
								return (
									<div key={key} className="relative min-w-0 flex-auto">
										<Link
											to={section.to as "/"}
											params={{ owner }}
											search={section.search}
											activeOptions={{ exact: true }}
											aria-current={active ? "page" : undefined}
											className={compactTabClass(active)}
										>
											<section.icon className="h-4 w-4 shrink-0" />
											{section.label}
										</Link>
										{key === "activity" && isMember ? (
											<AttentionNavBadge owner={owner} collapsed />
										) : null}
									</div>
								);
							})}
							{isMember ? (
								<Link
									to="/$owner/settings"
									params={{ owner }}
									aria-current={settings ? "page" : undefined}
									activeOptions={{ exact: true }}
									className={cn("min-w-0 flex-auto", compactTabClass(settings))}
								>
									<Settings className="h-4 w-4 shrink-0" aria-hidden="true" />
									Settings
								</Link>
							) : isMembershipLoading ? (
								<span
									role="status"
									aria-label="Loading workspace navigation"
									className={cn(
										"pointer-events-none min-w-0 flex-auto",
										compactTabClass(false),
									)}
								>
									<Skeleton
										aria-hidden="true"
										className="h-4 w-4 shrink-0 rounded-none"
									/>
									<span aria-hidden="true" className="relative">
										<span className="invisible">Settings</span>
										<Skeleton className="absolute inset-x-0 top-1/2 h-3 -translate-y-1/2 rounded-none" />
									</span>
								</span>
							) : null}
						</nav>
						<button
							type="button"
							onClick={openCommandPalette}
							aria-label="Search"
							title="Search (⌘K)"
							className="flex h-10 w-9 shrink-0 items-center justify-center rounded-md text-sidebar-foreground/70 transition-colors hover:bg-sidebar-accent/60 hover:text-sidebar-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
						>
							<LobuSearchIcon className="h-4 w-4" />
						</button>
					</>
				) : (
					<div className="flex-1" />
				)}
			</div>
		</TooltipProvider>
	);
}

const compactTabClass = (active: boolean) =>
	cn(
		"flex h-10 items-center justify-center gap-0.5 rounded-md px-0.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
		active
			? "bg-sidebar-accent text-sidebar-accent-foreground"
			: "text-sidebar-foreground/70 hover:bg-sidebar-accent/60 hover:text-sidebar-foreground",
	);

/** One sidebar item: icon + label, or the icon alone with its name in a
 *  tooltip while the sidebar is collapsed. */
const itemClass = (collapsed: boolean, active = false) =>
	cn(
		"flex items-center rounded-md text-sm transition-colors",
		collapsed ? "mx-auto h-9 w-9 justify-center" : "h-8 w-full gap-2.5 px-2.5",
		active
			? "bg-sidebar-accent font-medium text-sidebar-accent-foreground"
			: "text-sidebar-foreground/75 hover:bg-sidebar-accent/60 hover:text-sidebar-foreground",
	);

function Tip({
	label,
	show,
	children,
}: {
	label: string;
	show: boolean;
	children: ReactNode;
}) {
	if (!show) return children;
	return (
		<Tooltip>
			<TooltipTrigger asChild>{children}</TooltipTrigger>
			<TooltipContent side="right">{label}</TooltipContent>
		</Tooltip>
	);
}

function ItemButton({
	label,
	tip = label,
	icon: Icon,
	collapsed,
	onClick,
}: {
	label: string;
	tip?: string;
	icon: IconComponent;
	collapsed: boolean;
	onClick: () => void;
}) {
	return (
		<Tip label={tip} show>
			<button
				type="button"
				onClick={onClick}
				aria-label={label}
				className={cn(
					collapsed
						? itemClass(true)
						: "flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-sidebar-foreground/60 transition-colors hover:bg-sidebar-accent/60 hover:text-sidebar-foreground",
				)}
			>
				<Icon className="h-4 w-4" />
			</button>
		</Tip>
	);
}

function ItemLink({
	label,
	icon: Icon,
	to,
	params,
	search,
	hash,
	trailing,
	active,
	collapsed,
}: {
	label: string;
	icon: IconComponent;
	to: string;
	params?: Record<string, string>;
	search?: Record<string, unknown>;
	hash?: string;
	trailing?: ReactNode;
	active: boolean;
	collapsed: boolean;
}) {
	return (
		<Tip label={label} show={collapsed}>
			<Link
				to={to as "/"}
				params={params as never}
				search={search as never}
				hash={hash}
				// Active state is our own path rule; exact matching stops the router from
				// also marking parents current. Hash matching distinguishes legacy
				// #policies links from General at the same path and search.
				activeOptions={{ exact: true, includeHash: true }}
				aria-label={label}
				aria-current={active ? "page" : undefined}
				className={itemClass(collapsed, active)}
			>
				<Icon className={cn("shrink-0", collapsed ? "h-5 w-5" : "h-4 w-4")} />
				{collapsed ? null : (
					<span className="min-w-0 flex-1 truncate">{label}</span>
				)}
				{collapsed ? null : trailing}
			</Link>
		</Tip>
	);
}

/** The workspace's "needs attention" count on Home. It opens the Needs
 *  attention lens and is absent at zero. Shares the attention query (and
 *  cache) with the stream itself, so it costs no extra fetch. */
function AttentionNavBadge({
	owner,
	collapsed,
}: {
	owner: string;
	collapsed: boolean;
}) {
	const { attentionItems } = useAttentionInbox(owner);
	const count = attentionItems.length;
	if (count === 0) return null;
	return (
		<Link
			to="/$owner"
			params={{ owner }}
			search={{ attention: "1" }}
			activeOptions={{ exact: true }}
			aria-label={`${count} item${count === 1 ? "" : "s"} need attention`}
			title="Needs attention"
			className={cn(
				"absolute min-w-4 rounded-full bg-blue-500 px-1 text-center text-[10px] font-semibold leading-4 text-white",
				collapsed ? "-top-1 right-0.5" : "top-2 right-2.5",
			)}
		>
			{count}
		</Link>
	);
}

/** Account settings at the sidebar's foot; sign out lives on that page. */
function AccountLink({ collapsed }: { collapsed: boolean }) {
	const { pathname } = useLocation();
	const { session, isReady } = useAuthState();
	if (!isReady) return null;
	if (!session) {
		// Expanded, the sign-in card above carries the link.
		if (!collapsed) return null;
		return (
			<Tip label="Sign in" show>
				<Link
					to="/auth/login"
					search={signInSearch({
						callbackUrl: currentCallbackUrl(),
						intent: "sign-in",
					})}
					aria-label="Sign in"
					className={itemClass(true)}
				>
					<LogIn className="h-5 w-5" />
				</Link>
			</Tip>
		);
	}
	const userName = session.user.name?.trim() || "You";
	return (
		<Tip label="Account settings" show={collapsed}>
			<Link
				to="/account/settings"
				activeOptions={{ exact: true }}
				aria-label="Account settings"
				aria-current={pathname === "/account/settings" ? "page" : undefined}
				className={itemClass(collapsed, pathname === "/account/settings")}
			>
				<CircleUser
					className={cn("shrink-0", collapsed ? "h-5 w-5" : "h-4 w-4")}
				/>
				{collapsed ? null : <span className="truncate">{userName}</span>}
			</Link>
		</Tip>
	);
}

/** Workspace destinations and Settings share one navigation viewport. */
export function AppSidebar({
	collapsed = false,
	onToggleCollapsed,
}: {
	collapsed?: boolean;
	onToggleCollapsed?: () => void;
}) {
	const nav = useWorkspaceNavigation();
	const { owner, isMember, settings, location } = nav;
	const viewsErrorCopy = nav.viewsError
		? requestErrorCopy(nav.viewsError, { subject: "Views" })
		: null;
	const { session, isReady } = useAuthState();
	const sidebarRef = useRef<HTMLElement>(null);
	const previousMode = useRef(settings);
	useEffect(() => {
		if (previousMode.current !== settings) {
			const focused = document.activeElement;
			// Only move focus when navigation hid the control the user activated.
			if (
				focused instanceof HTMLElement &&
				sidebarRef.current?.contains(focused) &&
				focused.closest('[data-navigation-panel][aria-hidden="true"]')
			) {
				sidebarRef.current
					.querySelector<HTMLElement>(
						settings
							? '[data-navigation-panel="settings"] a'
							: '[data-navigation-panel="workspace"] [aria-label="Settings"]',
					)
					?.focus();
			}
		}
		previousMode.current = settings;
	}, [settings]);
	const panelClass =
		"flex min-h-0 flex-col transition-[translate,opacity] duration-[140ms] ease-[cubic-bezier(0.23,1,0.32,1)] motion-reduce:translate-x-0 motion-reduce:transition-none";
	return (
		<TooltipProvider delayDuration={200}>
			<aside
				ref={sidebarRef}
				data-sidebar=""
				className={cn(
					"fixed inset-y-0 left-0 z-30 flex flex-col border-r border-border bg-sidebar-background text-sidebar-foreground transition-[width] duration-200 motion-reduce:transition-none",
					collapsed ? "w-14" : "w-64",
				)}
			>
				<div
					className={cn(
						"flex h-14 shrink-0 items-center gap-1 border-b border-sidebar-border",
						collapsed ? "justify-center" : "px-2",
					)}
				>
					{collapsed ? (
						<OrganizationDropdown display="icon" />
					) : (
						<>
							<div className="min-w-0 flex-1">
								<OrganizationDropdown display="full" />
							</div>
							{owner ? (
								<ItemButton
									label="Search"
									tip="Search (⌘K)"
									icon={LobuSearchIcon}
									collapsed={false}
									onClick={openCommandPalette}
								/>
							) : null}
							{onToggleCollapsed ? (
								<ItemButton
									label="Collapse sidebar"
									icon={PanelLeftClose}
									collapsed={false}
									onClick={onToggleCollapsed}
								/>
							) : null}
						</>
					)}
				</div>
				{collapsed && owner ? (
					<div className="flex shrink-0 flex-col gap-1 py-2">
						<ItemButton
							label="Search"
							tip="Search (⌘K)"
							icon={LobuSearchIcon}
							collapsed
							onClick={openCommandPalette}
						/>
						{onToggleCollapsed ? (
							<ItemButton
								label="Expand sidebar"
								icon={PanelLeftOpen}
								collapsed
								onClick={onToggleCollapsed}
							/>
						) : null}
					</div>
				) : null}
				{owner ? (
					<div
						className="grid min-h-0 flex-1 overflow-hidden"
						data-navigation-mode={settings ? "settings" : "workspace"}
					>
						<div
							data-navigation-panel="workspace"
							inert={settings}
							aria-hidden={settings}
							style={{ gridArea: "1 / 1" }}
							className={cn(
								panelClass,
								settings
									? "invisible -translate-x-2 opacity-0"
									: "visible translate-x-0 opacity-100",
							)}
						>
							<div className="min-h-0 flex-1 overflow-y-auto py-3">
								<nav
									aria-label="Workspace sections"
									className={cn("flex flex-col gap-1", !collapsed && "px-3")}
								>
									{PRIMARY_SECTIONS.map((key) => {
										const section = OWNER_SECTIONS[key];
										return (
											<div key={key} className="relative">
												<ItemLink
													label={section.label}
													icon={section.icon}
													to={section.to}
													params={{ owner }}
													search={section.search}
													active={
														isOwnerSectionActive(
															key,
															owner,
															location.pathname,
														) &&
														!(
															key === "context" &&
															location.pathname.includes("/data/-/views/")
														)
													}
													collapsed={collapsed}
												/>
												{key === "activity" && isMember ? (
													<AttentionNavBadge
														owner={owner}
														collapsed={collapsed}
													/>
												) : null}
											</div>
										);
									})}
									{nav.views.map((view) => (
										<ItemLink
											key={view.key}
											label={view.name}
											icon={LayoutDashboard}
											to="/$owner/data/-/views/$viewKey"
											params={{ owner, viewKey: view.key }}
											active={
												location.pathname.replace(/\/$/, "") ===
												`/${owner}/data/-/views/${encodeURIComponent(view.key)}`
											}
											collapsed={collapsed}
										/>
									))}
								</nav>
								{nav.viewsError && viewsErrorCopy && !collapsed ? (
									<div
										role="status"
										className="px-5 py-3 text-xs text-muted-foreground"
										data-error-code={
											nav.viewsError instanceof ApiError
												? nav.viewsError.code
												: undefined
										}
									>
										{viewsErrorCopy.title}
										{viewsErrorCopy.retryable ? (
											<>
												{" "}
												<button
													type="button"
													onClick={nav.retryViews}
													className="underline underline-offset-2"
												>
													Retry
												</button>
											</>
										) : null}
									</div>
								) : null}
							</div>
							{!collapsed && isMember ? (
								<OnboardingChecklist key={owner} owner={owner} />
							) : null}
							{isMember ? (
								<div
									className={cn(
										"shrink-0 border-t border-sidebar-border py-2",
										!collapsed && "px-3",
									)}
								>
									<ItemLink
										label="Settings"
										icon={Settings}
										to="/$owner/settings/organization"
										params={{ owner }}
										active={false}
										collapsed={collapsed}
										trailing={
											<ChevronRight className="h-4 w-4 text-sidebar-foreground/50" />
										}
									/>
								</div>
							) : null}
						</div>
						<div
							data-navigation-panel="settings"
							inert={!settings}
							aria-hidden={!settings}
							style={{ gridArea: "1 / 1" }}
							className={cn(
								panelClass,
								settings
									? "visible translate-x-0 opacity-100"
									: "invisible translate-x-2 opacity-0",
							)}
						>
							<div className={cn("shrink-0 py-3", !collapsed && "px-3")}>
								<ItemLink
									label="Back to workspace"
									icon={ArrowLeft}
									to={nav.returnTo.pathname}
									search={nav.returnTo.search}
									hash={nav.returnTo.hash}
									active={false}
									collapsed={collapsed}
								/>
							</div>
							<nav
								aria-label="Settings sections"
								className={cn(
									"flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto pb-3",
									!collapsed && "px-3",
								)}
							>
								{nav.destinations.map((d) => (
									<div key={d.key}>
										{d.separator ? (
											<div
												aria-hidden
												className="mx-2 my-3 h-px bg-sidebar-border"
											/>
										) : null}
										<ItemLink
											label={d.label}
											icon={d.icon}
											to={d.to}
											params={d.params}
											search={d.search}
											active={d.active}
											collapsed={collapsed}
										/>
									</div>
								))}
							</nav>
						</div>
					</div>
				) : (
					<div className="flex flex-1 items-center justify-center px-4 text-center text-xs text-sidebar-foreground/50">
						{collapsed ? null : "Pick an organization to continue."}
					</div>
				)}
				{!collapsed && isReady && !session ? <SidebarSignIn /> : null}
				<div
					className={cn(
						"flex shrink-0 flex-col gap-0.5 border-t border-sidebar-border py-2",
						!collapsed && "px-3",
					)}
				>
					<AccountLink collapsed={collapsed} />
				</div>
			</aside>
		</TooltipProvider>
	);
}
